import React, { useCallback, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../hook';
import { fetchCustomers, fetchDevices, fetchDeviceTypes, fetchDeviceUpdates } from './_slice';
import { Card, Popover, Space, Table, TableProps } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { appConfig } from '../../common/config';
import LayoutStandard from '../../layouts/layout_standard';
import { EnumDeviceSearchType, IEventSearchParams } from '../../common/models';
import { convertDayjsToISOString, formatDateTime, getTimeRangeFromNow, isPastTime } from '../../common/utils';
import EventSearchForm from './features/event_search_form';
import { convertDeviceUpdates, getUpdateStatusName } from '../../common/helper';
import CopyToClipboard from '../../components/copy_clipboard';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

const columns: TableProps<any>['columns'] = [
  {
    title: 'Created At',
    dataIndex: ['DateCreated', 'DateTimeUtc'],
    key: 'CreatedAt',
    render: formatDateTime,
  },
  {
    title: 'Updated At',
    dataIndex: 'Messages',
    key: 'UpdatedAt',
    render: (val, record) => {
      const updatedTimes: Date[] = [];
      val.forEach(
        (msg: any) => {
          if (msg.DateAcked) {
            updatedTimes.push(new Date(msg.DateAcked.DateTimeUtc));
          } else if (msg.DateFailed) {
            updatedTimes.push(new Date(msg.DateFailed.DateTimeUtc));
          } else if (record.DateCreated) {
            updatedTimes.push(new Date(record.DateCreated.DateTimeUtc));
          }
        }
      );
      const oldestDate = updatedTimes.reduce((oldest, current) =>
        current < oldest ? current : oldest
      );
      return <>{oldestDate ? oldestDate.toLocaleString('en-NZ') : ''}</>;
    },
  },
  {
    title: 'Update Id',
    dataIndex: 'UpdateId',
    key: 'UpdateId',
    render: (val) => (
      <>
        {val} <CopyToClipboard content={val} />
      </>
    ),
  },
  {
    title: 'Serial Number',
    dataIndex: 'SerialNumber',
    key: 'SerialNumber',
    render: (val, record) => <>
      <Link to={`/devices/searchType/Device ID or Serial/keywords/${val}`}>
        <span style={{color: record.DeactivationTime ? '#cccccc' : 'inherit'}}><b>{val}</b></span>
      </Link> <CopyToClipboard content={val} />
    </>,
  },
  {
    title: 'Device Name',
    dataIndex: 'DeviceName',
    key: 'DeviceName',
  },
  {
    title: 'Device Type',
    dataIndex: 'DeviceTypeName',
    key: 'DeviceTypeName',
    width: 180,
    sorter: (a, b) => a.DeviceTypeName > b.DeviceTypeName ? 1 : -1,
  },
  {
    title: 'Settings',
    dataIndex: 'ReadableSettings',
    key: 'ReadableSettings',
    render: (val) => {
      return <>
        {val.map((setting: any, index: number) => (
          <div key={index}><b>{setting.name}:</b> {setting.value}</div>
        ))}
      </>;
    },
  },
  {
    title: 'Downlink Results',
    dataIndex: 'Messages',
    key: 'Messages',
    render: (val, record) => {
      const popoverContent = (
        <Card>
          <div style={{ display: 'flex', marginBottom: 16, gap: 20, }}>
            <div><b>Device Id:</b> {record.DeviceId} <CopyToClipboard content={record.DeviceId} /></div>
            <div><b>Network Id:</b> {record.NetworkId} <CopyToClipboard content={record.NetworkId} /></div>
            <div><b>Update Id:</b> {record.UpdateId} <CopyToClipboard content={record.UpdateId} /></div>
          </div>
          <Table<any>
            dataSource={val}
            rowKey='MessageId'
            columns={[
              {
                title: 'Message Id', dataIndex: 'MessageId', key: 'MessageId',
                render: (v) => <>{v} <CopyToClipboard content={v} /></>
              },
              { title: 'Payload', dataIndex: 'Payload', key: 'Payload' },
              { title: 'Retry Attempts', dataIndex: 'RetryAttempt', key: 'RetryAttempt' },
              { title: 'Sent At', dataIndex: 'DateSent', key: 'DateSent', render: (v) => v ? formatDateTime(v.DateTimeUtc) : 'N/A' },
              { title: 'Failed At', dataIndex: 'DateFailed', key: 'DateFailed', render: (v) => v ? formatDateTime(v.DateTimeUtc) : 'N/A' },
              { title: 'Acked At', dataIndex: 'DateAcked', key: 'DateAcked', render: (v) => v ? formatDateTime(v.DateTimeUtc) : 'N/A' },
              { title: 'Status', dataIndex: 'State', key: 'State', render: (v) => getUpdateStatusName(v) },
            ]}
            pagination={false}
          />
        </Card>
      );

      return <Popover content={popoverContent}>
        {
          val.map(
            (msg: any, index: number) => {
              return (
                <div key={index} style={{ display: 'flex', gap: 10, cursor: 'pointer' }}>
                    {msg.DateAcked ? <div style={{color:'green'}}><CheckCircleOutlined /> <b>Acked</b></div> : ''}
                    {
                      (!msg.DateAcked && !msg.DateFailed) && <>
                      {
                        isPastTime(msg.ExpiresAt?.DateTimeUtc)
                          ? <div style={{color:'red'}}><CloseCircleOutlined /> <b>Failed</b></div>
                          : <div style={{color:'orange'}}><ClockCircleOutlined /> <b>Waiting to Ack</b></div>
                      }
                      </>
                    }
                </div>
              );
            }
          )
        }
      </Popover>;
    },
  },
];

interface RouteParams {
  searchType: EnumDeviceSearchType;
  keywords: string;
  [key: string]: string;
}

interface IDeviceUpdatesPageProps {
}

const DeviceUpdatesPage: React.FC<IDeviceUpdatesPageProps> = () => {
  const customerState = useAppSelector((state) => state.customer);
  const dispatch = useAppDispatch();
  const { searchType, keywords } = useParams<RouteParams>();
  const [searchParams, setSearchParams] = useState<IEventSearchParams>({
    searchType: searchType as EnumDeviceSearchType ?? EnumDeviceSearchType.customerName,
    keywords: keywords ?? '',
  });

  // Function to fetch events and devices based on search params
  const fetchData = useCallback(async (params: IEventSearchParams) => {
    await dispatch(fetchDevices(params));
    await dispatch(fetchDeviceUpdates(params));
  }, [dispatch]);

  // Function to handle search or export
  const onSearch = useCallback(async (params: IEventSearchParams) => {
    setSearchParams(params);
    if (params.timeRange) {
      params.startTime = convertDayjsToISOString(params.timeRange?.[0]);
      params.endTime = convertDayjsToISOString(params.timeRange?.[1]);
    }
    await fetchData(params);
  }, [fetchData]);

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchDeviceTypes());
  }, [dispatch]);

  useEffect(() => {
    if (!customerState.customers.length) return;
    const newSearchType = searchType || EnumDeviceSearchType.customerName;
    const newKeywords = newSearchType === EnumDeviceSearchType.customerName && !keywords
      ? customerState.customers[0].Name
      : keywords;

    // TODO: There is an issue we can fix in the future.
    // Because if the device has not reported inthe past 7 days then the UI looks weird as their is no data.
    // Maybe we do the end time is always today, but the start time is 7 days before the most recent message.
    // This means most devices will show 7 days, but devices having issues will clearly show when it stopped reporting,
    // and you can see big blank space to make it clear that it has stopped reporting.
    const newTimeRange = [
      EnumDeviceSearchType.deviceIdSerial,
      EnumDeviceSearchType.networkId,
    ].includes(newSearchType as EnumDeviceSearchType)
      ? getTimeRangeFromNow(7)
      : undefined;

    onSearch({
      searchType: newSearchType,
      keywords: newKeywords,
      timeRange: newTimeRange,
    });

  }, [customerState.customers, onSearch, searchType, keywords]);

  const deviceUpdates = convertDeviceUpdates(customerState.devices, customerState.deviceTypes, customerState.deviceUpdates);

  return (
    <LayoutStandard page={appConfig.pages.deviceUpdates} count={customerState.deviceUpdates.length}>
      <Space direction='vertical' size="middle" style={{ display: 'flex' }}>
        <Card size='small'>
          <EventSearchForm
              searchParams={searchParams}
              customers={customerState.customers}
              onSearch={(params) => onSearch(params)}
              onExport={(params) => onSearch(params)}
              hideExport={true}
            />
        </Card>
        <Table<any>
          columns={columns}
          dataSource={deviceUpdates}
          rowKey='UpdateId'
          pagination={{ position: ['bottomCenter'] }}
          loading={customerState.isLoadingDeviceUpdates || customerState.isLoadingDevices}
          scroll={{ x: 'max-content' }}
        />
      </Space>
    </LayoutStandard>
  );
};

export default DeviceUpdatesPage;
